import { moveSlidingToggle, afterTogglePaint } from './sliding-toggle.js';
import { fetchCollectionText } from './collection-request.js?v=20261008-filter-performance';
import { isArrivalCollection, prepareArrivalCollection, isFreshestCollectionUrl, prepareFreshestCollection } from './collection-freshest-sort.js?v=20261029-arrivals';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const compactLayout = window.matchMedia('(max-width: 749px)');
const BROWSING_MODE_STORAGE_KEY = 'sigma-coffee-browsing-mode';
const COLLECTION_LOAD_MORE_CONTROLLER = Symbol.for('sigma.collectionLoadMoreController');
const COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER = Symbol.for('sigma.collectionActiveLoadMoreController');
const COLLECTION_SHOW_ALL_CONTROLLER = Symbol.for('sigma.collectionShowAllController');
const COLLECTION_ACTIVE_SHOW_ALL_CONTROLLER = Symbol.for('sigma.collectionActiveShowAllController');
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const visibleFocusableElements = (container) =>
  [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
    (element) =>
      element instanceof HTMLElement &&
      element.getAttribute('aria-disabled') !== 'true' &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.getClientRects().length > 0,
  );

const collectionSeriesTarget = (link) => {
  if (
    !(link instanceof HTMLAnchorElement) ||
    !link.classList.contains('collection-series-shortcuts__link') ||
    link.hasAttribute('download') ||
    (link.target && link.target.toLowerCase() !== '_self')
  ) {
    return null;
  }

  const destination = new URL(link.href, window.location.href);
  if (
    destination.origin !== window.location.origin ||
    destination.pathname !== window.location.pathname ||
    destination.search !== window.location.search ||
    !destination.hash
  ) {
    return null;
  }

  try {
    const target = document.getElementById(decodeURIComponent(destination.hash.slice(1)));
    return target instanceof HTMLElement && target.classList.contains('collection-series')
      ? { destination, target }
      : null;
  } catch {
    return null;
  }
};

const scrollToCollectionSeries = ({ destination, target }, keyboardActivation) => {
  const header = document.querySelector('[data-header-scroll-shell]');
  const headerHeight = header instanceof HTMLElement
    ? Math.max(0, Math.ceil(header.getBoundingClientRect().height))
    : 0;
  const rootStyles = window.getComputedStyle(document.documentElement);
  const targetGap = Number.parseFloat(rootStyles.getPropertyValue('--sc-space-3')) || 0;
  const scrollingDown = target.getBoundingClientRect().top > targetGap;
  const scrollOffset = targetGap + (scrollingDown ? 0 : headerHeight);

  target.style.setProperty(
    '--collection-series-scroll-offset',
    `${scrollOffset}px`,
  );

  if (window.location.hash !== destination.hash) {
    window.history.replaceState(window.history.state, '', destination);
  }

  if (keyboardActivation) {
    const heading = target.querySelector('.collection-series__heading');
    if (heading instanceof HTMLElement) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  }

  target.scrollIntoView({
    behavior: reducedMotion.matches ? 'instant' : 'smooth',
    block: 'start',
  });
};

const initialCardBatchSize = () => {
  if (window.innerWidth >= 1400) return 16;
  if (window.innerWidth >= 1024) return 12;
  if (window.innerWidth >= 750) return 8;
  return 6;
};

const loadMoreBatchSize = () => 32;

const collectionCards = (results) =>
  [...results.querySelectorAll(':scope > .product-card')].filter((card) => card instanceof HTMLElement);

const nextPaginationUrl = (pagination) => {
  const nextLink = pagination?.querySelector('.next a, a[rel="next"]');
  return nextLink instanceof HTMLAnchorElement ? new URL(nextLink.href, window.location.href) : null;
};

const setCollectionLoadingControl = (control, loading) => {
  if (!(control instanceof HTMLElement)) {
    return;
  }

  control.dispatchEvent(new CustomEvent('sigma:button-loading', {
    bubbles: true,
    detail: { loading },
  }));
};

const cardVariantForMode = (mode) => {
  const theme = document.documentElement.dataset.theme === 'light' ? 'Light' : 'Dark';
  const detail = mode === 'advanced' ? 'Advanced' : 'Basic';

  return `${theme}${detail}`;
};

const readCollectionDocumentMetadata = (collection) => {
  const payload = collection?.querySelector('[data-collection-document-metadata]');

  if (!(payload instanceof HTMLScriptElement)) {
    return null;
  }

  try {
    const metadata = JSON.parse(payload.textContent || '');

    if (
      typeof metadata.title !== 'string' ||
      !metadata.title ||
      typeof metadata.socialTitle !== 'string' ||
      !metadata.socialTitle ||
      typeof metadata.description !== 'string' ||
      typeof metadata.socialDescription !== 'string' ||
      !metadata.socialDescription ||
      typeof metadata.canonicalUrl !== 'string' ||
      !metadata.canonicalUrl
    ) {
      return null;
    }

    const canonicalUrl = new URL(metadata.canonicalUrl, window.location.origin);
    canonicalUrl.searchParams.delete('section_id');

    // Shopify page_title can already contain HTML entities. Decode once before
    // assigning DOM text, matching escape_once in the server-rendered head.
    const decodeTitle = (value) => {
      const text = document.createElement('textarea');
      text.innerHTML = value.replaceAll('<', '&lt;');
      return text.value;
    };

    return {
      ...metadata,
      title: decodeTitle(metadata.title),
      socialTitle: decodeTitle(metadata.socialTitle),
      canonicalUrl: canonicalUrl.toString(),
    };
  } catch {
    return null;
  }
};

const setNamedMetaContent = (selector, attributes, content) => {
  let element = document.head.querySelector(selector);

  if (!content) {
    element?.remove();
    return;
  }

  if (!(element instanceof HTMLMetaElement)) {
    element = document.createElement('meta');
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    document.head.append(element);
  }

  element.content = content;
};

const updateCollectionDocumentMetadata = (metadata) => {
  document.title = metadata.title;

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!(canonical instanceof HTMLLinkElement)) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.append(canonical);
  }
  canonical.href = metadata.canonicalUrl;

  setNamedMetaContent('meta[name="description"]', { name: 'description' }, metadata.description);
  setNamedMetaContent('meta[property="og:title"]', { property: 'og:title' }, metadata.socialTitle);
  setNamedMetaContent('meta[property="og:url"]', { property: 'og:url' }, metadata.canonicalUrl);
  setNamedMetaContent(
    'meta[property="og:description"]',
    { property: 'og:description' },
    metadata.socialDescription,
  );
  setNamedMetaContent('meta[name="twitter:title"]', { name: 'twitter:title' }, metadata.socialTitle);
  setNamedMetaContent(
    'meta[name="twitter:description"]',
    { name: 'twitter:description' },
    metadata.socialDescription,
  );
};

const readStoredMode = () => {
  try {
    return window.localStorage.getItem(BROWSING_MODE_STORAGE_KEY) === 'advanced'
      ? 'advanced'
      : 'basic';
  } catch {
    return 'basic';
  }
};

const storeMode = (mode) => {
  try {
    window.localStorage.setItem(BROWSING_MODE_STORAGE_KEY, mode);
  } catch {
    // Browsing preferences remain functional when storage is unavailable.
  }
};

const setCardVisibility = (card, mode) => {
  const advanced = mode === 'advanced';
  const advancedFacts = card.querySelector('[data-product-advanced-facts]');
  const advancedReveal = card.querySelector('[data-product-advanced-reveal]');
  const basicSummary = card.querySelector('[data-product-basic-summary]');

  [advancedFacts, advancedReveal].forEach((panel) => {
    if (!(panel instanceof HTMLElement)) {
      return;
    }

    panel.inert = !advanced;
    panel.setAttribute('aria-hidden', String(!advanced));
  });

  if (basicSummary instanceof HTMLElement) {
    basicSummary.inert = advanced;
    basicSummary.setAttribute('aria-hidden', String(advanced));
  }
};

let collectionViewRequest = null;
let collectionLoadingControl = null;

const navigateToCollectionUrl = (url) => {
  const link = document.createElement('a');
  link.href = url.toString();
  link.hidden = true;
  link.dataset.pageLoadingPriority = '';
  document.body.append(link);
  link.click();
};

export const updateCollectionView = async ({
  collection,
  url,
  updateHistory = true,
  focusSelector = null,
  scrollToResults = false,
  loadingControl = null,
  responseCache = null,
} = {}) => {
  const view = collection?.querySelector('[data-collection-view]');
  const result = collection?.querySelector('[data-collection-result-count]');
  const status = collection?.querySelector('[data-collection-update-status]');

  if (
    !(collection instanceof HTMLElement) ||
    !(view instanceof HTMLElement) ||
    !(result instanceof HTMLElement) ||
    !(url instanceof URL)
  ) {
    if (url instanceof URL) {
      navigateToCollectionUrl(url);
    }
    return { committed: false, fallback: true };
  }

  collectionViewRequest?.abort();
  setCollectionLoadingControl(collectionLoadingControl, false);
  collection.dispatchEvent(new CustomEvent('sigma:collection-view:updating', {
    bubbles: true,
    detail: { collection, url },
  }));
  const request = new AbortController();
  collectionViewRequest = request;
  collectionLoadingControl = loadingControl;
  const requestUrl = new URL(url);
  requestUrl.searchParams.set('section_id', result.dataset.collectionSectionId || '');
  // Keep current results usable for quick responses. Shape the fallback from
  // these real cards only if the request is still pending after 140ms.
  let layoutApi = null;
  const showLoadingLayout = () => {
    if (collectionViewRequest !== request || request.signal.aborted) return;
    view.removeAttribute('data-collection-view-loading');
    const placeholder = view.querySelector('[data-collection-loading-skeleton]');
    const results = view.querySelector('[data-collection-results]');
    if (placeholder && results && layoutApi) {
      const layout = layoutApi.captureLoadingLayout(results);
      if (layout && layoutApi.renderLoadingLayout(placeholder, layout)) {
        const products = view.querySelector('.collection-products');
        placeholder.style.setProperty('--loading-layout-inline-offset', `${Math.max(0, layout.originX - products.getBoundingClientRect().left)}px`);
      }
    }
    view.dataset.collectionViewLoading = '';
  };
  const resizeLoadingLayout = () => {
    if (view.hasAttribute('data-collection-view-loading')) showLoadingLayout();
  };
  const loadingTimer = window.setTimeout(() => {
    showLoadingLayout();
    void import('./loading-layout.js').then((api) => {
      layoutApi = api;
      showLoadingLayout();
    }).catch(() => {});
  }, 140);
  window.addEventListener('resize', resizeLoadingLayout, { passive: true });
  collection.setAttribute('aria-busy', 'true');
  setCollectionLoadingControl(loadingControl, true);

  if (status instanceof HTMLElement) {
    status.textContent = `Updating ${result.dataset.resultNounPlural || 'products'}.`;
  }

  try {
    let html = responseCache?.take?.(requestUrl) || null;
    const responseSource = html === null ? 'network' : 'preview-cache';

    if (html === null) {
      const { response, text } = await fetchCollectionText(requestUrl, {
        headers: { Accept: 'text/html' },
        signal: request.signal,
      });

      if (!response.ok) {
        throw new Error(`Collection request failed with ${response.status}`);
      }

      html = text;
    }

    const sectionDocument = new DOMParser().parseFromString(html, 'text/html');
    // DOMParser exposes noscript fallback controls even when scripting is enabled.
    sectionDocument.querySelectorAll('noscript').forEach((fallback) => fallback.remove());
    const nextCollection = sectionDocument.querySelector('.collection-shell');
    const nextView = nextCollection?.querySelector('[data-collection-view]');
    const nextResult = nextView?.querySelector('[data-collection-result-count]');
    const nextDocumentMetadata = readCollectionDocumentMetadata(nextCollection);

    if (
      !(nextCollection instanceof HTMLElement) ||
      !(nextView instanceof HTMLElement) ||
      !(nextResult instanceof HTMLElement) ||
      !nextDocumentMetadata
    ) {
      throw new Error('Collection response was incomplete');
    }

    await prepareArrivalCollection({
      collection: nextCollection, url, signal: request.signal, batchSize: loadMoreBatchSize,
      initialBatchSize: () => Math.max(initialCardBatchSize(), Number(nextCollection.dataset.collectionInitialCount) || 0),
    });
    await prepareFreshestCollection({
      collection: nextCollection,
      url,
      signal: request.signal,
      batchSize: loadMoreBatchSize,
      initialBatchSize: initialCardBatchSize,
    });

    if (request.signal.aborted || collectionViewRequest !== request) {
      return { committed: false, fallback: false, aborted: true };
    }

    collection[COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER]?.dispose?.();
    delete collection.dataset.collectionSortFailed;
    nextView.dataset.collectionViewTransition = 'entering';
    view.replaceWith(nextView);
    collection.dataset.showSoldOut = nextCollection.dataset.showSoldOut || 'false';

    if (updateHistory) {
      const historyUrl = new URL(url);
      historyUrl.searchParams.delete('section_id');
      const currentHistoryState = { ...window.history.state };
      delete currentHistoryState.sigmaCollectionShowAll;
      window.history.pushState(
        { ...currentHistoryState, sigmaCollectionBrowsing: true },
        '',
        historyUrl,
      );
    }

    updateCollectionDocumentMetadata(nextDocumentMetadata);

    collection.dispatchEvent(new CustomEvent('sigma:collection-view:updated', {
      bubbles: true,
      detail: { collection, view: nextView, nextCollection },
    }));

    if (status instanceof HTMLElement) {
      const summary = nextResult.textContent.trim().replace(/\s+/g, ' ');
      status.textContent = `${summary}. Results updated.`;
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
    nextView.removeAttribute('data-collection-view-transition');

    if (scrollToResults) {
      collection.querySelector('.collection-products')?.scrollIntoView({ block: 'start' });
    }

    if (focusSelector) {
      const focusTarget = collection.querySelector(focusSelector);
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus({ preventScroll: true });
      }
    }

    return { committed: true, fallback: false, responseSource };
  } catch (error) {
    if (request.signal.aborted || collectionViewRequest !== request) {
      return { committed: false, fallback: false, aborted: true };
    }

    if (isFreshestCollectionUrl(url) || isArrivalCollection(collection, url)) {
      collection.dataset.collectionSortFailed = '';
      if (status instanceof HTMLElement) {
        status.textContent = `${isFreshestCollectionUrl(url) ? 'Freshest roast' : 'Newest arrivals'} could not be loaded. Results were not changed. Choose the sort again to retry.`;
      }
      return { committed: false, fallback: false };
    }

    navigateToCollectionUrl(url);
    return { committed: false, fallback: true };
  } finally {
    window.clearTimeout(loadingTimer);
    window.removeEventListener('resize', resizeLoadingLayout);
    if (collectionViewRequest === request) {
      collectionViewRequest = null;
      delete document.documentElement.dataset.collectionArrivalPending;
      delete document.documentElement.dataset.collectionInitialLoading;
      collection.removeAttribute('aria-busy');
      const currentView = collection.querySelector('[data-collection-view]');
      currentView?.removeAttribute('data-collection-view-loading');
      collection[COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER]?.resume?.();
      setCollectionLoadingControl(collectionLoadingControl, false);
      collectionLoadingControl = null;
    }
  }
};

const initializeCollectionLoadMore = (root = document) => {
  root.querySelectorAll('[data-collection-load-more]').forEach((control) => {
    if (!(control instanceof HTMLElement) || control[COLLECTION_LOAD_MORE_CONTROLLER]) return;
    if (control.hasAttribute('data-collection-freshest-load-more') || isFreshestCollectionUrl(new URL(window.location.href)) || isArrivalCollection(control.closest('.collection-shell'), new URL(window.location.href))) return;

    const results = control.closest('[data-collection-results]');
    const collection = control.closest('.collection-shell');
    const button = control.querySelector('[data-collection-load-more-button]');
    const label = control.querySelector('[data-collection-load-more-label]');
    const status = control.querySelector('[data-collection-load-more-status]');
    const resultCount = collection?.querySelector('[data-collection-result-count]');

    if (
      !(results instanceof HTMLElement) ||
      !(collection instanceof HTMLElement) ||
      !(button instanceof HTMLButtonElement) ||
      !(label instanceof HTMLElement) ||
      !(status instanceof HTMLElement) ||
      !(resultCount instanceof HTMLElement)
    ) {
      return;
    }

    let pagination = results.querySelector('.collection-pagination');
    let loading = false;
    let initialised = false;
    let pageRequest = null;
    let disposed = false;
    let loadMoreRevision = 0;
    let pausedForShowAll = false;

    const reportedTotal = Number(resultCount.dataset.collectionResultCount || 0);
    const countsReadyCards = resultCount.dataset.collectionResultCountMode === 'rendered';
    let countsRenderedCards = countsReadyCards;
    const currentPage = Number(
      resultCount.dataset.collectionCurrentPage
      || pagination?.querySelector('[aria-current="page"]')?.textContent
      || 1,
    );
    const configuredInitialCount = Number(collection.dataset.collectionInitialCount);
    const initialCount = Number.isInteger(configuredInitialCount) && configuredInitialCount > 0
      ? configuredInitialCount
      : null;
    const startedOnFirstPage = currentPage === 1;
    const noun = reportedTotal === 1
      ? resultCount.dataset.resultNoun || 'product'
      : resultCount.dataset.resultNounPlural || 'products';

    const cards = () => collectionCards(results);
    const visibleCards = () => cards().filter((card) => !card.hidden);
    const hiddenCards = () => cards().filter((card) => card.hidden);
    const syncCardCountMode = (loadedCount, hasNextPage) => {
      if (
        countsRenderedCards
        || !(
          loadedCount > reportedTotal
          || (hasNextPage && loadedCount >= reportedTotal)
          || (startedOnFirstPage && !hasNextPage && loadedCount !== reportedTotal)
        )
      ) {
        return false;
      }

      countsRenderedCards = true;
      return true;
    };

    const updateControl = (message = '') => {
      const visibleCount = visibleCards().length;
      const loadedCount = cards().length;
      const hasNextPage = nextPaginationUrl(pagination) !== null;
      const hasMore = hiddenCards().length > 0 || hasNextPage;

      if (syncCardCountMode(loadedCount, hasNextPage)) message = '';

      if (countsRenderedCards) {
        const renderedQualifier = countsReadyCards ? 'ready ' : '';
        const loadedNoun = loadedCount === 1
          ? resultCount.dataset.resultNoun || 'product'
          : resultCount.dataset.resultNounPlural || 'products';
        const countIsComplete = startedOnFirstPage && !hasNextPage;

        resultCount.dataset.collectionResultCount = String(loadedCount);
        resultCount.textContent = countIsComplete
          ? `${loadedCount} ${renderedQualifier}${loadedNoun} available`
          : hasNextPage
            ? `${loadedCount} ${renderedQualifier}${loadedNoun} loaded; more may be available`
            : `${loadedCount} ${renderedQualifier}${loadedNoun} loaded; other pages may contain more`;
        control.hidden = !hasMore;
        if (pagination instanceof HTMLElement) pagination.hidden = false;
        button.hidden = !hasMore;
        label.textContent = hiddenCards().length > 0
          ? `Load ${Math.min(loadMoreBatchSize(), hiddenCards().length)} more`
          : 'Load more';
        status.textContent = message || (
          hasMore
            ? `Showing ${visibleCount} ${renderedQualifier}${visibleCount === 1 ? resultCount.dataset.resultNoun || 'product' : resultCount.dataset.resultNounPlural || 'products'}. More may be available.`
            : countIsComplete
              ? `All ${visibleCount} ${renderedQualifier}${visibleCount === 1 ? resultCount.dataset.resultNoun || 'product' : resultCount.dataset.resultNounPlural || 'products'} shown.`
              : `${visibleCount} ${renderedQualifier}${visibleCount === 1 ? resultCount.dataset.resultNoun || 'product' : resultCount.dataset.resultNounPlural || 'products'} shown on the loaded pages.`
        );
        if (pausedForShowAll) button.hidden = true;
        return;
      }

      const remainingCount = Math.max(0, reportedTotal - visibleCount);
      control.hidden = reportedTotal === 0;
      if (pagination instanceof HTMLElement) pagination.hidden = true;

      button.hidden = !hasMore || remainingCount === 0;
      label.textContent = `Load ${Math.min(loadMoreBatchSize(), remainingCount)} more`;

      status.textContent = message || (
        remainingCount === 0
          ? `All ${reportedTotal} ${noun} shown.`
          : `Showing ${visibleCount} of ${reportedTotal} ${noun}.`
      );
      if (pausedForShowAll) button.hidden = true;
    };

    const revealCards = (amount) => {
      const scrollPosition = { left: window.scrollX, top: window.scrollY };
      const revealed = hiddenCards().slice(0, amount);
      revealed.forEach((card) => card.removeAttribute('hidden'));
      if (revealed.length > 0) window.scrollTo(scrollPosition);
      return revealed;
    };

    const fetchNextPage = async () => {
      const url = nextPaginationUrl(pagination);
      if (!(url instanceof URL)) return [];

      url.searchParams.set('section_id', resultCount.dataset.collectionSectionId || '');
      setCollectionLoadingControl(button, true);
      const request = new AbortController();
      pageRequest = request;

      try {
        const { response, text: html } = await fetchCollectionText(url, {
          headers: { Accept: 'text/html' },
          signal: request.signal,
        });
        if (!response.ok) throw new Error(`Collection request failed with ${response.status}`);
        const pageDocument = new DOMParser().parseFromString(html, 'text/html');
        pageDocument.querySelectorAll('noscript').forEach((fallback) => fallback.remove());
        const nextResults = pageDocument.querySelector('[data-collection-results]');
        if (!(nextResults instanceof HTMLElement)) throw new Error('Collection response was incomplete');

        if (request.signal.aborted || pageRequest !== request) {
          throw new DOMException('Superseded load-more request', 'AbortError');
        }

        const nextCards = collectionCards(nextResults);
        const nextPagination = nextResults.querySelector('.collection-pagination');
        if (countsRenderedCards && nextCards.length > 0) {
          results.querySelector('[data-collection-availability-empty]')?.remove();
        }
        nextCards.forEach((card) => {
          card.dataset.collectionLoadMoreCard = '';
          card.hidden = true;
          (pagination || control).before(card);
        });

        if (pagination instanceof HTMLElement) {
          if (nextPagination instanceof HTMLElement) {
            nextPagination.hidden = true;
            pagination.replaceWith(nextPagination);
            pagination = nextPagination;
          } else {
            pagination.remove();
            pagination = null;
          }
        }

        collection.dispatchEvent(new CustomEvent('sigma:collection-view:updated', {
          bubbles: true,
          detail: { collection, view: collection.querySelector('[data-collection-view]') },
        }));

        return nextCards;
      } finally {
        if (pageRequest === request) pageRequest = null;
      }
    };

    const loadMore = async (event) => {
      if (loading || collection.getAttribute('aria-busy') === 'true') return;
      loading = true;
      button.disabled = true;
      const operationRevision = ++loadMoreRevision;
      const operationIsCurrent = () =>
        !disposed &&
        operationRevision === loadMoreRevision &&
        collection.getAttribute('aria-busy') !== 'true';

      const target = loadMoreBatchSize();
      const keyboardActivation = event.detail === 0;
      const visibleBefore = new Set(visibleCards());
      const newlyVisibleCards = () => visibleCards().filter((card) => !visibleBefore.has(card));
      let attempts = 0;

      try {
        while (newlyVisibleCards().length < target) {
          if (!operationIsCurrent()) return;
          revealCards(target - newlyVisibleCards().length);
          if (newlyVisibleCards().length >= target || !(nextPaginationUrl(pagination) instanceof URL)) break;
          if (attempts >= 3) break;
          attempts += 1;
          await fetchNextPage();
          if (!operationIsCurrent()) return;
        }

        if (!operationIsCurrent()) return;

        const newlyVisible = newlyVisibleCards();
        const firstLink = newlyVisible[0]?.querySelector('[data-product-card-link]');

        const visibleCount = visibleCards().length;
        syncCardCountMode(cards().length, nextPaginationUrl(pagination) !== null);
        if (countsRenderedCards) {
          const hasMore = hiddenCards().length > 0 || nextPaginationUrl(pagination) !== null;
          const renderedNoun = visibleCount === 1
            ? resultCount.dataset.resultNoun || 'product'
            : resultCount.dataset.resultNounPlural || 'products';
          if (countsReadyCards) {
            updateControl(
              newlyVisible.length > 0
                ? `${newlyVisible.length} more loaded. ${visibleCount} ready ${renderedNoun} shown${hasMore ? '; more may be available.' : '.'}`
                : hasMore
                  ? 'No additional ready coffees were found on those pages. More pages remain.'
                  : startedOnFirstPage
                    ? `No additional ready coffees were found. All ${visibleCount} ready ${renderedNoun} shown.`
                    : `No additional ready coffees were found. ${visibleCount} ready ${renderedNoun} shown on the loaded pages.`,
            );
          } else {
            updateControl(
              newlyVisible.length > 0
                ? `${newlyVisible.length} more loaded. ${visibleCount} ${renderedNoun} shown${hasMore ? '; more may be available.' : '.'}`
                : hasMore
                  ? `No additional ${renderedNoun} were found on those pages. More pages remain.`
                  : `${visibleCount} ${renderedNoun} shown.`,
            );
          }
        } else {
          const remainingCount = Math.max(0, reportedTotal - visibleCount);
          updateControl(
            remainingCount === 0
              ? `${newlyVisible.length} more loaded. All ${reportedTotal} ${noun} shown.`
              : `${newlyVisible.length} more loaded. Showing ${visibleCount} of ${reportedTotal} ${noun}.`,
          );
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));

        if (!operationIsCurrent()) return;

        if (keyboardActivation && firstLink instanceof HTMLElement) {
          firstLink.focus({ preventScroll: true });
        }
      } catch (error) {
        if (error?.name === 'AbortError' || !operationIsCurrent()) return;

        control.hidden = false;
        button.hidden = true;
        if (pagination instanceof HTMLElement) pagination.hidden = false;
        status.textContent = 'More products could not be loaded. Use the page links below to continue.';

        if (keyboardActivation) {
          const recoveryLink = pagination?.querySelector('a[href]');
          if (recoveryLink instanceof HTMLElement) recoveryLink.focus({ preventScroll: true });
        }
      } finally {
        if (operationRevision === loadMoreRevision) {
          loading = false;
          button.disabled = disposed || collection.getAttribute('aria-busy') === 'true';
          setCollectionLoadingControl(button, false);
        }
      }
    };

    const syncBatch = () => {
      const currentCards = cards();
      currentCards.forEach((card) => {
        card.dataset.collectionLoadMoreCard = '';
      });

      if (!initialised) {
        currentCards.slice(initialCount ?? initialCardBatchSize()).forEach((card) => {
          card.hidden = true;
        });
        initialised = true;
      } else {
        const shortfall = initialCardBatchSize() - visibleCards().length;
        if (shortfall > 0) revealCards(shortfall);
      }

      updateControl();
    };

    button.addEventListener('click', loadMore);
    const suspend = () => {
      loadMoreRevision += 1;
      pageRequest?.abort();
      pageRequest = null;
      loading = false;
      button.disabled = true;
      setCollectionLoadingControl(button, false);
    };
    const resume = () => {
      if (!disposed) button.disabled = false;
    };
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      suspend();
      collection.removeEventListener('sigma:collection-view:updating', suspend);
      if (collection[COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER] === controller) {
        delete collection[COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER];
      }
    };
    const pauseForShowAll = () => {
      pausedForShowAll = true;
      suspend();
      button.hidden = true;
    };
    const restoreAfterShowAll = () => {
      if (disposed) return;
      pausedForShowAll = false;
      syncBatch();
      resume();
    };
    const adoptPagination = (nextPagination) => {
      pagination = nextPagination instanceof HTMLElement ? nextPagination : null;
    };
    const controller = {
      adoptPagination,
      syncBatch,
      dispose,
      resume,
      suspend,
      pauseForShowAll,
      restoreAfterShowAll,
    };
    collection[COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER]?.dispose?.();
    collection[COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER] = controller;
    collection.addEventListener('sigma:collection-view:updating', suspend);
    control[COLLECTION_LOAD_MORE_CONTROLLER] = controller;
    syncBatch();
  });
};

initializeCollectionLoadMore();

document.addEventListener('sigma:collection-view:updated', (event) => {
  if (event.detail?.appendOnly) return;
  initializeCollectionLoadMore(event.target);
});

const collectionShowAllHistoryKey = (url = new URL(window.location.href)) => {
  const keyUrl = new URL(url);
  keyUrl.hash = '';
  keyUrl.searchParams.delete('section_id');
  return `${keyUrl.pathname}${keyUrl.search}`;
};

const collectionCardIdentity = (card) => {
  const value = card.dataset.productCardUrl
    || card.querySelector('[data-product-card-link], a[href]')?.getAttribute('href')
    || '';

  if (!value) return null;

  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return value;
  }
};

const initializeCollectionShowAll = (root = document) => {
  root.querySelectorAll('[data-collection-show-all]').forEach((control) => {
    if (!(control instanceof HTMLElement) || control[COLLECTION_SHOW_ALL_CONTROLLER]) return;

    const collection = control.closest('.collection-shell');
    const results = collection?.querySelector('[data-collection-results]');
    if (results?.hasAttribute('data-collection-freshest-results') || isFreshestCollectionUrl(new URL(window.location.href)) || isArrivalCollection(results?.closest('.collection-shell'), new URL(window.location.href))) return;
    const resultCount = collection?.querySelector('[data-collection-result-count]');
    const button = control.querySelector('[data-collection-show-all-button]');
    const label = control.querySelector('[data-collection-show-all-label]');
    const status = control.querySelector('[data-collection-show-all-status]');
    const feedback = control.querySelector('[data-collection-show-all-feedback]');
    const progressElement = control.querySelector('[data-collection-show-all-progress]');
    const progressCount = control.querySelector('[data-collection-show-all-count]');

    if (
      !(collection instanceof HTMLElement)
      || !(results instanceof HTMLElement)
      || !(resultCount instanceof HTMLElement)
      || !(button instanceof HTMLButtonElement)
      || !(label instanceof HTMLElement)
      || !(status instanceof HTMLElement)
    ) {
      return;
    }

    let pagination = results.querySelector('.collection-pagination');
    let request = null;
    let revision = 0;
    let disposed = false;
    let active = false;
    let phase = 'idle';
    let restoreScrollY = null;
    let replayHistoryState = null;
    const seenPageUrls = new Set();
    const cardIdentities = new Set();
    let loadedCardCount = 0;
    let lastAnnouncedProgressBucket = -1;
    let reportedTotal = Number(resultCount.dataset.collectionResultCount || 0);
    const defaultLabel = button.dataset.collectionShowAllLabelDefault || 'Show all coffees';
    const usesCoffeeNoun = /coffee/i.test(defaultLabel);
    const pluralNoun = usesCoffeeNoun ? 'coffees' : 'results';
    const singularNoun = usesCoffeeNoun ? 'coffee' : 'result';
    const initialPage = Number(
      resultCount.dataset.collectionCurrentPage
      || pagination?.querySelector('[aria-current="page"]')?.textContent
      || 1,
    );

    const cards = () => collectionCards(results);
    const nounFor = (count) => count === 1 ? singularNoun : pluralNoun;
    const totalForProgress = () => Number.isFinite(reportedTotal) && reportedTotal >= loadedCardCount
      ? reportedTotal
      : null;
    const nativeController = () => collection[COLLECTION_ACTIVE_LOAD_MORE_CONTROLLER];

    const setFeedbackPhase = (nextPhase) => {
      if (!(feedback instanceof HTMLElement)) return;
      feedback.hidden = nextPhase === 'idle';
      feedback.dataset.collectionShowAllFeedbackState = nextPhase;
    };

    const syncProgress = () => {
      const total = totalForProgress();

      if (progressElement instanceof HTMLProgressElement) {
        if (total !== null && total > 0) {
          progressElement.max = total;
          progressElement.value = Math.min(loadedCardCount, total);
        } else {
          progressElement.removeAttribute('value');
        }
      }

      if (progressCount instanceof HTMLElement) {
        const countText = total !== null && total > loadedCardCount
          ? `${loadedCardCount} of ${total} ${nounFor(total)} loaded`
          : `${loadedCardCount} ${nounFor(loadedCardCount)} loaded`;
        const phaseLabel = {
          running: 'Loading',
          stopped: 'Stopped',
          error: 'Loading paused',
          complete: 'Complete',
        }[phase];
        progressCount.textContent = phase === 'running' && total !== null
          ? `${loadedCardCount} of ${total}`
          : phaseLabel ? `${phaseLabel} · ${countText}` : countText;
      }
    };

    const saveHistory = () => {
      if (!active || disposed) return;

      window.history.replaceState({
        ...window.history.state,
        sigmaCollectionBrowsing: true,
        sigmaCollectionShowAll: {
          active: true,
          key: collectionShowAllHistoryKey(),
          phase: replayHistoryState?.phase || phase,
          loadedCount: replayHistoryState?.loadedCount || loadedCardCount,
          scrollY: replayHistoryState?.scrollY ?? restoreScrollY ?? window.scrollY,
        },
      }, '', window.location.href);
    };

    const announceProgress = (prefix = '', { force = false } = {}) => {
      const total = totalForProgress();
      const progressBucket = total !== null && total > 0
        ? Math.floor((loadedCardCount / total) * 10)
        : Math.floor(loadedCardCount / 50);

      syncProgress();
      if (!force && progressBucket <= lastAnnouncedProgressBucket) return;

      lastAnnouncedProgressBucket = Math.max(lastAnnouncedProgressBucket, progressBucket);
      const progress = total !== null && total > loadedCardCount
        ? `${loadedCardCount} of ${total} ${nounFor(total)} loaded.`
        : `${loadedCardCount} ${nounFor(loadedCardCount)} loaded.`;
      status.textContent = prefix ? `${prefix} ${progress}` : progress;
    };

    const restoreScroll = () => {
      if (!Number.isFinite(restoreScrollY)) return;
      const scrollY = restoreScrollY;
      restoreScrollY = null;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => window.scrollTo({ left: window.scrollX, top: scrollY }));
      });
    };

    const setRestingPhase = (nextPhase, message = '') => {
      replayHistoryState = null;
      phase = nextPhase;
      setFeedbackPhase(nextPhase);
      control.removeAttribute('data-collection-show-all-running');
      setCollectionLoadingControl(button, false);
      nativeController()?.restoreAfterShowAll?.();

      if (nextPhase === 'complete') {
        syncProgress();
        status.textContent = `All ${loadedCardCount} ${nounFor(loadedCardCount)} shown.`;
        if (document.activeElement === button) status.focus({ preventScroll: true });
        button.hidden = true;
      } else {
        button.hidden = false;
        label.textContent = nextPhase === 'error' ? 'Retry loading' : 'Resume loading';
        announceProgress(
          message || (nextPhase === 'error' ? 'Loading paused.' : 'Loading stopped.'),
          { force: true },
        );
      }

      saveHistory();
      restoreScroll();
    };

    const syncLoadedCardRegistry = () => {
      const loadedCards = cards();
      loadedCardCount = loadedCards.length;
      loadedCards.forEach((card) => {
        const identity = collectionCardIdentity(card);
        if (identity) cardIdentities.add(identity);
      });
      return loadedCards;
    };

    const revealLoadedCards = () => {
      const scrollPosition = { left: window.scrollX, top: window.scrollY };
      let changed = false;
      syncLoadedCardRegistry().forEach((card) => {
        if (!card.hidden) return;
        card.removeAttribute('hidden');
        changed = true;
      });
      if (changed && restoreScrollY === null) window.scrollTo(scrollPosition);
    };

    const replacePagination = (nextPagination) => {
      if (pagination instanceof HTMLElement) {
        if (nextPagination instanceof HTMLElement) {
          nextPagination.hidden = true;
          pagination.replaceWith(nextPagination);
          pagination = nextPagination;
        } else {
          pagination.remove();
          pagination = null;
        }
      } else if (nextPagination instanceof HTMLElement) {
        nextPagination.hidden = true;
        results.append(nextPagination);
        pagination = nextPagination;
      }

      nativeController()?.adoptPagination?.(pagination);
    };

    const appendPage = (nextResults) => {
      const nextCards = collectionCards(nextResults);
      const nextPagination = nextResults.querySelector('.collection-pagination');
      const appendedCards = [];
      const fragment = document.createDocumentFragment();

      nextCards.forEach((card) => {
        const identity = collectionCardIdentity(card);
        if (identity && cardIdentities.has(identity)) return;
        if (identity) cardIdentities.add(identity);
        card.dataset.collectionLoadMoreCard = '';
        card.removeAttribute('hidden');
        card.querySelectorAll('img').forEach((image) => {
          image.setAttribute('loading', 'lazy');
          image.setAttribute('fetchpriority', 'low');
        });
        appendedCards.push(card);
        fragment.append(card);
      });

      loadedCardCount += appendedCards.length;
      if (appendedCards.length > 0) {
        const insertionTarget = pagination || results.querySelector('[data-collection-load-more]');
        if (insertionTarget instanceof HTMLElement) insertionTarget.before(fragment);
        else results.append(fragment);
        collection.dispatchEvent(new CustomEvent('sigma:collection-view:updated', {
          bubbles: true,
          detail: {
            appendOnly: true,
            collection,
            cards: appendedCards,
            view: collection.querySelector('[data-collection-view]') || collection,
          },
        }));
      }
      replacePagination(nextPagination);
    };

    const fetchNextPage = async (operationRevision) => {
      const url = nextPaginationUrl(pagination);
      if (!(url instanceof URL)) return false;

      const pageKey = url.toString();
      if (seenPageUrls.has(pageKey)) throw new Error('Collection pagination repeated a page');
      seenPageUrls.add(pageKey);
      url.searchParams.set('section_id', resultCount.dataset.collectionSectionId || '');
      const pageRequest = new AbortController();
      request = pageRequest;
      const { response, text: html } = await fetchCollectionText(url, {
        headers: { Accept: 'text/html' },
        signal: pageRequest.signal,
      });
      if (!response.ok) throw new Error(`Collection request failed with ${response.status}`);

      const pageDocument = new DOMParser().parseFromString(html, 'text/html');
      pageDocument.querySelectorAll('noscript').forEach((fallback) => fallback.remove());
      const nextResults = pageDocument.querySelector('[data-collection-results]');
      if (!(nextResults instanceof HTMLElement)) throw new Error('Collection response was incomplete');
      if (pageRequest.signal.aborted || operationRevision !== revision || disposed) {
        throw new DOMException('Superseded show-all request', 'AbortError');
      }

      const nextResultCount = pageDocument.querySelector('[data-collection-result-count]');
      const nextReportedTotal = Number(nextResultCount?.dataset.collectionResultCount);
      reportedTotal = Number.isFinite(nextReportedTotal) && nextReportedTotal >= 0
        ? nextReportedTotal
        : null;
      appendPage(nextResults);
      if (request === pageRequest) request = null;
      return true;
    };

    const run = async ({ targetCount = 0, terminalPhase = 'running', savedScrollY = null } = {}) => {
      if (disposed || phase === 'running') return;

      pagination = results.querySelector('.collection-pagination');
      nativeController()?.adoptPagination?.(pagination);
      active = true;
      phase = 'running';
      setFeedbackPhase('running');
      control.setAttribute('data-collection-show-all-running', '');
      restoreScrollY = Number.isFinite(savedScrollY) ? savedScrollY : null;
      const operationRevision = ++revision;
      seenPageUrls.clear();
      button.hidden = false;
      button.disabled = false;
      label.textContent = 'Stop loading';
      nativeController()?.pauseForShowAll?.();
      revealLoadedCards();
      replayHistoryState = targetCount > loadedCardCount
        ? {
            phase: terminalPhase,
            loadedCount: targetCount,
            scrollY: Number.isFinite(savedScrollY) ? savedScrollY : window.scrollY,
          }
        : null;
      announceProgress('', { force: true });
      saveHistory();

      try {
        while (!disposed && operationRevision === revision) {
          const hasReachedRestoredDepth = targetCount > 0 && loadedCardCount >= targetCount;
          if (hasReachedRestoredDepth && replayHistoryState) {
            replayHistoryState = null;
            saveHistory();
          }
          if (restoreScrollY !== null && (targetCount === 0 || hasReachedRestoredDepth)) {
            restoreScroll();
          }
          if (hasReachedRestoredDepth && (terminalPhase === 'stopped' || terminalPhase === 'error')) {
            setRestingPhase(terminalPhase);
            return;
          }

          if (!(nextPaginationUrl(pagination) instanceof URL)) {
            setRestingPhase('complete');
            return;
          }

          await fetchNextPage(operationRevision);
          if (disposed || operationRevision !== revision) return;
          announceProgress();
          saveHistory();
        }
      } catch (error) {
        if (error?.name === 'AbortError' || disposed || operationRevision !== revision) return;
        request = null;
        setRestingPhase('error', `More ${pluralNoun} could not be loaded.`);
      }
    };

    const stop = () => {
      if (phase !== 'running') return;
      revision += 1;
      request?.abort();
      request = null;
      setRestingPhase('stopped');
    };

    const start = ({ focusButton = false } = {}) => {
      if (focusButton) button.focus({ preventScroll: true });
      run();
    };

    const activate = async (event) => {
      if (phase === 'running') {
        stop();
        return;
      }

      if (initialPage > 1 && !active) {
        setFeedbackPhase('running');
        button.disabled = true;
        setCollectionLoadingControl(button, true);
        status.textContent = `Preparing all ${pluralNoun} from the first page.`;
        nativeController()?.pauseForShowAll?.();
        const firstPageUrl = new URL(window.location.href);
        firstPageUrl.searchParams.delete('page');
        const update = await updateCollectionView({
          collection,
          url: firstPageUrl,
          loadingControl: button,
        });

        if (update.committed) {
          collection[COLLECTION_ACTIVE_SHOW_ALL_CONTROLLER]?.start?.({
            focusButton: event.detail === 0,
          });
        }
        return;
      }

      run();
    };

    const suspendForPageHide = () => {
      if (!active) return;
      saveHistory();
      revision += 1;
      request?.abort();
      request = null;
      setCollectionLoadingControl(button, false);
    };

    const restoreFromHistory = () => {
      const saved = window.history.state?.sigmaCollectionShowAll;
      if (
        !saved?.active
        || saved.key !== collectionShowAllHistoryKey()
        || !['running', 'stopped', 'error', 'complete'].includes(saved.phase)
      ) {
        return;
      }

      active = true;
      phase = 'idle';
      run({
        targetCount: Number(saved.loadedCount || 0),
        terminalPhase: saved.phase,
        savedScrollY: Number(saved.scrollY),
      });
    };

    const dispose = () => {
      if (disposed) return;
      disposed = true;
      control.removeAttribute('data-collection-show-all-running');
      revision += 1;
      request?.abort();
      request = null;
      setCollectionLoadingControl(button, false);
      collection.removeEventListener('sigma:collection-view:updating', dispose);
      if (collection[COLLECTION_ACTIVE_SHOW_ALL_CONTROLLER] === controller) {
        delete collection[COLLECTION_ACTIVE_SHOW_ALL_CONTROLLER];
      }
    };

    const controller = {
      dispose,
      restoreFromHistory,
      start,
      suspendForPageHide,
    };
    const hasMore = cards().some((card) => card.hidden) || nextPaginationUrl(pagination) instanceof URL;
    if (!hasMore) {
      control.hidden = true;
      button.hidden = true;
      return;
    }

    button.addEventListener('click', activate);
    collection[COLLECTION_ACTIVE_SHOW_ALL_CONTROLLER]?.dispose?.();
    collection[COLLECTION_ACTIVE_SHOW_ALL_CONTROLLER] = controller;
    collection.addEventListener('sigma:collection-view:updating', dispose);
    control[COLLECTION_SHOW_ALL_CONTROLLER] = controller;
    control.hidden = false;
    button.hidden = false;
    button.disabled = false;
    label.textContent = defaultLabel;
    syncLoadedCardRegistry();
    setFeedbackPhase('idle');
    syncProgress();
    queueMicrotask(restoreFromHistory);
  });
};

initializeCollectionShowAll();

document.addEventListener('sigma:collection-view:updated', (event) => {
  if (event.detail?.appendOnly) return;
  initializeCollectionShowAll(event.target);
});

window.addEventListener('pagehide', () => {
  document.querySelectorAll('[data-collection-show-all]').forEach((control) => {
    control[COLLECTION_SHOW_ALL_CONTROLLER]?.suspendForPageHide?.();
  });
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('[data-collection-show-all]').forEach((control) => {
    control[COLLECTION_SHOW_ALL_CONTROLLER]?.restoreFromHistory?.();
  });
});

let loadMoreResizeFrame = 0;
window.addEventListener('resize', () => {
  cancelAnimationFrame(loadMoreResizeFrame);
  loadMoreResizeFrame = requestAnimationFrame(() => {
    document.querySelectorAll('[data-collection-load-more]').forEach((control) => {
      control[COLLECTION_LOAD_MORE_CONTROLLER]?.syncBatch();
    });
  });
}, { passive: true });

if (document.querySelector('[data-collection-view]')) {
  window.history.replaceState(
    { ...window.history.state, sigmaCollectionBrowsing: true },
    '',
    window.location.href,
  );
}

window.addEventListener('popstate', (event) => {
  if (!event.state?.sigmaCollectionBrowsing) {
    return;
  }

  const collection = document.querySelector('.collection-shell');

  if (!(collection instanceof HTMLElement)) {
    return;
  }

  const url = new URL(window.location.href);
  updateCollectionView({ collection, url, updateHistory: false });
});

document.addEventListener('click', (event) => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest('a[href]');
  const seriesTarget = collectionSeriesTarget(link);

  if (seriesTarget) {
    event.preventDefault();
    scrollToCollectionSeries(seriesTarget, event.detail === 0);
    return;
  }

  const stockFilterClear = target?.closest('[data-collection-stock-clear]');
  const stockCollection = stockFilterClear?.closest('.collection-shell');

  if (stockFilterClear instanceof HTMLButtonElement && stockCollection instanceof HTMLElement) {
    event.preventDefault();
    const url = new URL(window.location.href);
    url.searchParams.delete('show_sold_out');
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.delete('page');

    updateCollectionView({
      collection: stockCollection,
      url,
      focusSelector: '[data-collection-filter-open]',
      loadingControl: stockCollection.querySelector('[data-collection-filter-open]'),
    });
    return;
  }

  const collection = link?.closest('.collection-shell');
  const isPagination = Boolean(link?.closest('.collection-pagination'));

  if (
    !(link instanceof HTMLAnchorElement) ||
    !(collection instanceof HTMLElement) ||
    (!link.hasAttribute('data-collection-update') && !isPagination)
  ) {
    return;
  }

  event.preventDefault();
  const linkUrl = new URL(link.href, window.location.href);
  const url = isPagination ? new URL(window.location.href) : linkUrl;

  if (isFreshestCollectionUrl(new URL(window.location.href))) {
    url.searchParams.set('freshest', '1');
    url.searchParams.set('sort_by', 'created-descending');
  }

  if (isPagination) {
    const page = linkUrl.searchParams.get('page');

    if (page) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
  }

  const activeFilterChip = link.classList.contains('collection-filter-state__value');
  updateCollectionView({
    collection,
    url,
    focusSelector: isPagination
      ? '[data-collection-result-count]'
      : link.dataset.collectionUpdateFocus || '[data-collection-filter-open]',
    scrollToResults: isPagination,
    loadingControl: activeFilterChip
      ? collection.querySelector('[data-collection-filter-open]')
      : link,
  });
});

export const initializeCollectionSheet = ({ trigger: initialTrigger, dialog, panel, backdrop, closeButton }) => {
  if (
    !(initialTrigger instanceof HTMLButtonElement) ||
    !(dialog instanceof HTMLDialogElement) ||
    typeof dialog.showModal !== 'function' ||
    !(panel instanceof HTMLElement) ||
    !(backdrop instanceof HTMLElement) ||
    !(closeButton instanceof HTMLButtonElement)
  ) {
    return null;
  }

  let trigger = initialTrigger;
  let panelAnimation = null;
  let backdropAnimation = null;
  let closeTimer = 0;

  const panelOffset = (opening) => {
    return `translate3d(0, 0, 0) scale(${opening ? 0.97 : 0.985})`;
  };

  const runMotion = (opening) => {
    panelAnimation?.cancel();
    backdropAnimation?.cancel();

    if (reducedMotion.matches || typeof panel.animate !== 'function') {
      return null;
    }

    const duration = opening ? 260 : 180;
    const easing = opening
      ? 'cubic-bezier(0.215, 0.61, 0.355, 1)'
      : 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    const currentPanel = window.getComputedStyle(panel);
    const panelFrom = opening
      ? { opacity: 0, transform: panelOffset(true) }
      : {
          opacity: currentPanel.opacity,
          transform: currentPanel.transform === 'none'
            ? 'translate3d(0, 0, 0) scale(1)'
            : currentPanel.transform,
        };
    const panelTo = opening
      ? { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }
      : { opacity: 0, transform: panelOffset(false) };

    panelAnimation = panel.animate([panelFrom, panelTo], {
      duration,
      easing,
      fill: 'both',
    });
    backdropAnimation = backdrop.animate(
      [
        { opacity: opening ? 0 : window.getComputedStyle(backdrop).opacity },
        { opacity: opening ? 1 : 0 },
      ],
      { duration, easing, fill: 'both' },
    );

    return panelAnimation;
  };

  const finishClose = (animation = null) => {
    window.clearTimeout(closeTimer);

    if (animation && panelAnimation !== animation) {
      return;
    }

    panelAnimation?.cancel();
    backdropAnimation?.cancel();
    panelAnimation = null;
    backdropAnimation = null;
    dialog.close();
  };

  const closeDialog = () => {
    if (!dialog.open || dialog.dataset.sheetState === 'closing') {
      return;
    }

    dialog.dataset.sheetState = 'closing';
    const animation = runMotion(false);

    if (!animation) {
      finishClose();
      return;
    }

    animation.finished.then(() => finishClose(animation)).catch(() => {});
    closeTimer = window.setTimeout(() => finishClose(animation), 260);
  };

  const openDialog = () => {
    window.clearTimeout(closeTimer);
    delete dialog.dataset.sheetState;

    if (!dialog.open) {
      dialog.showModal();
    }

    trigger.setAttribute('aria-expanded', 'true');
    panel.scrollTop = 0;
    closeButton.focus({ preventScroll: true });

    const animation = runMotion(true);
    animation?.finished
      .then(() => {
        if (panelAnimation === animation) {
          animation.cancel();
          backdropAnimation?.cancel();
          panelAnimation = null;
          backdropAnimation = null;
        }
      })
      .catch(() => {});
  };

  const bindTrigger = (nextTrigger) => {
    if (!(nextTrigger instanceof HTMLButtonElement) || nextTrigger === trigger) {
      return nextTrigger === trigger;
    }

    trigger = nextTrigger;
    trigger.addEventListener('click', openDialog);
    return true;
  };

  trigger.addEventListener('click', openDialog);
  closeButton.addEventListener('click', closeDialog);
  backdrop.addEventListener('click', closeDialog);

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = visibleFocusableElements(dialog);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements.at(-1);

    if (!firstFocusable || !lastFocusable) {
      return;
    }

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  });

  dialog.addEventListener('close', () => {
    window.clearTimeout(closeTimer);
    delete dialog.dataset.sheetState;
    trigger.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => {
      if (!dialog.open) {
        trigger.focus({ preventScroll: true });
      }
    }, 0);
  });

  return {
    bindTrigger,
    close: closeDialog,
    getTrigger: () => trigger,
  };
};

const initializedSortDialogs = new WeakMap();

const syncCollectionSortDialog = (sortDialog, nextCollection) => {
  const url = new URL(window.location.href);
  const currentSort = url.searchParams.get('sort_by');
  const freshestActive = isFreshestCollectionUrl(url);
  const sortOptions = [...sortDialog.querySelectorAll('[data-collection-sort-url]')].filter(
    (option) => option instanceof HTMLAnchorElement,
  );
  const nextActiveOption = nextCollection?.querySelector('[data-collection-sort-url][aria-current="true"]');
  const activeOption = (freshestActive
    ? sortOptions.find((option) => option.dataset.collectionSortOption === 'freshest')
    : null) || sortOptions.find((option) =>
    nextActiveOption && option.dataset.collectionSortOption === nextActiveOption.dataset.collectionSortOption,
  ) || sortOptions.find((option) => {
    const target = new URL(option.href, window.location.origin);
    if (option.dataset.collectionSortDestination) {
      return target.pathname === url.pathname;
    }
    return target.pathname === url.pathname && target.searchParams.get('sort_by') === currentSort;
  })
    || sortOptions.find((option) => option.getAttribute('aria-current') === 'true');

  sortDialog.removeAttribute('aria-busy');
  sortOptions.forEach((option) => {
    const target = new URL(option.href, window.location.origin);
    if (target.pathname === url.pathname && !option.dataset.collectionSortDestination) {
      const nextUrl = new URL(url);
      const sortBy = target.searchParams.get('sort_by');
      nextUrl.searchParams.delete('page');
      nextUrl.searchParams.delete('freshest');
      if (sortBy) nextUrl.searchParams.set('sort_by', sortBy);
      else nextUrl.searchParams.delete('sort_by');
      if (option.hasAttribute('data-collection-sort-custom')) {
        nextUrl.searchParams.set('freshest', '1');
        nextUrl.searchParams.set('sort_by', 'created-descending');
      }
      if (option.hasAttribute('data-search-stock-order')) {
        const unavailableProducts = target.searchParams.get('options[unavailable_products]');
        if (unavailableProducts) {
          nextUrl.searchParams.set('options[unavailable_products]', unavailableProducts);
        } else {
          nextUrl.searchParams.delete('options[unavailable_products]');
        }
      }
      option.href = nextUrl.toString();
    }
    const active = option === activeOption;
    option.removeAttribute('aria-disabled');
    if (active) {
      option.setAttribute('aria-current', 'true');
    } else {
      option.removeAttribute('aria-current');
    }
  });

  const filterFreshest = sortDialog.closest('.collection-shell')?.querySelector('[data-collection-freshest-filter]');
  if (filterFreshest instanceof HTMLInputElement) filterFreshest.disabled = !freshestActive;
  const trigger = sortDialog.closest('.collection-shell')?.querySelector('[data-collection-sort-open]');
  const activeLabel = activeOption?.querySelector('strong')?.textContent.trim() || 'Selected option';
  const triggerLabel = trigger?.querySelector('[data-collection-sort-label]');
  if (trigger instanceof HTMLButtonElement) trigger.setAttribute('aria-label', `Sort by, currently ${activeLabel}`);
  if (triggerLabel instanceof HTMLElement) triggerLabel.textContent = activeLabel;
};

const initializeCollectionSort = (collection, nextCollection) => {
  const sortTrigger = collection.querySelector('[data-collection-sort-open]');
  const sortDialog = collection.querySelector('[data-collection-sort-dialog]');

  if (!(sortDialog instanceof HTMLDialogElement)) {
    return;
  }

  const existingSort = initializedSortDialogs.get(sortDialog);

  if (existingSort) {
    syncCollectionSortDialog(sortDialog, nextCollection);
    existingSort.sheet.bindTrigger(sortTrigger);
    return;
  }

  const sortSheet = initializeCollectionSheet({
    trigger: sortTrigger,
    dialog: sortDialog,
    panel: sortDialog.querySelector('[data-collection-sort-panel]'),
    backdrop: sortDialog.querySelector('[data-collection-sort-dismiss]'),
    closeButton: sortDialog.querySelector('[data-collection-sort-close]'),
  });

  if (!sortSheet) {
    return;
  }

  initializedSortDialogs.set(sortDialog, { sheet: sortSheet });
  const sortOptions = [...sortDialog.querySelectorAll('[data-collection-sort-url]')].filter(
    (option) => option instanceof HTMLAnchorElement,
  );

  syncCollectionSortDialog(sortDialog, nextCollection);

  sortOptions.forEach((option) => {
    option.addEventListener('click', async (event) => {
      if (option.getAttribute('aria-current') === 'true' && !collection.hasAttribute('data-collection-sort-failed')) {
        event.preventDefault();
        sortSheet.close();
        return;
      }

      const url = new URL(option.href, window.location.origin);
      const currentUrl = new URL(window.location.href);

      if (url.pathname !== currentUrl.pathname) {
        sortSheet.close();
        return;
      }

      event.preventDefault();
      url.searchParams.delete('page');
      const beforeOrder = [...collection.querySelectorAll('[data-collection-results] .product-card')]
        .map((card) => card.dataset.productCardUrl
          || card.querySelector('a[href]')?.getAttribute('href')?.split('?')[0]
          || '');
      const sortLabel = option.querySelector('strong')?.textContent.trim() || 'Selected option';
      sortDialog.setAttribute('aria-busy', 'true');
      sortOptions.forEach((sortOption) => {
        sortOption.setAttribute('aria-disabled', 'true');
      });
      sortSheet.close();
      const update = await updateCollectionView({
        collection,
        url,
        focusSelector: '[data-collection-sort-open]',
        loadingControl: sortSheet.getTrigger(),
      });

      if (update.committed) {
        const afterOrder = [...collection.querySelectorAll('[data-collection-results] .product-card')]
          .map((card) => card.dataset.productCardUrl
            || card.querySelector('a[href]')?.getAttribute('href')?.split('?')[0]
            || '');
        const orderChanged = beforeOrder.join('|') !== afterOrder.join('|');
        const status = collection.querySelector('[data-collection-update-status]');

        if (status instanceof HTMLElement) {
          status.textContent = orderChanged
            ? `Sorted by ${sortLabel}. Results updated.`
            : `Sorted by ${sortLabel}. The product order is unchanged for this catalogue.`;
        }
      } else if (!update.aborted) {
        syncCollectionSortDialog(sortDialog);
      }
    });
  });

  sortDialog.addEventListener('keydown', (event) => {
    const activeIndex = sortOptions.indexOf(document.activeElement);

    if (activeIndex < 0) {
      return;
    }

    let nextIndex = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = (activeIndex + 1) % sortOptions.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (activeIndex - 1 + sortOptions.length) % sortOptions.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = sortOptions.length - 1;
    }

    if (nextIndex === null || nextIndex === activeIndex) {
      return;
    }

    event.preventDefault();
    sortOptions[nextIndex].focus();
  });
};

document.querySelectorAll('[data-collection-browsing]').forEach((collection) => {
  if (!(collection instanceof HTMLElement)) {
    return;
  }

  const getCards = () => [...collection.querySelectorAll('[data-product-card-showcase]')].filter(
    (card) => card instanceof HTMLElement,
  );
  const switcher = collection.querySelector('[data-collection-mode-switcher]');
  const modeButtons = collection.querySelectorAll('[data-collection-mode-button]');
  const status = collection.querySelector('[data-collection-mode-status]');
  const preferenceOptions = collection.querySelectorAll('[data-collection-preference-option]');
  let profileFillFrame = 0;
  let profileFillSecondFrame = 0;

  const restartProfileFill = (cards, mode) => {
    cancelAnimationFrame(profileFillFrame);
    cancelAnimationFrame(profileFillSecondFrame);
    const animatedCards = [];
    cards.forEach((card) => {
      const animate = mode === 'advanced' && !reducedMotion.matches
        && card.hasAttribute('data-product-card-motion');
      card.dataset.productCardProfileFill = mode === 'advanced' && !animate ? 'on' : 'off';
      if (animate) animatedCards.push(card);
    });
    if (!animatedCards.length) return;

    profileFillFrame = requestAnimationFrame(() => {
      profileFillSecondFrame = requestAnimationFrame(() => {
        animatedCards.forEach((card) => {
          if (card.dataset.productCardMode === 'advanced') {
            card.dataset.productCardProfileFill = 'on';
          }
        });
      });
    });
  };

  const applyModeToCards = (cards, mode) => {
    cards.forEach((card) => {
      card.dataset.productCardMode = mode;
      card.dataset.productCardVariant = cardVariantForMode(mode);
      setCardVisibility(card, mode);
    });
  };

  const setMode = (mode, { announce = false, restartProfile = true, onApplied = () => {} } = {}) => {
    const safeMode = mode === 'advanced' ? 'advanced' : 'basic';
    const currentMode = collection.dataset.collectionBrowsingMode || 'basic';

    moveSlidingToggle(switcher?.querySelector('.collection-mode-switcher__indicator'), safeMode === 'advanced' ? 1 : 0, restartProfile);
    collection.dataset.collectionBrowsingMode = safeMode;

    const filterTrigger = collection.querySelector('[data-collection-filter-open]');
    if (filterTrigger instanceof HTMLButtonElement) {
      const filterCount = Number.parseInt(filterTrigger.dataset.collectionFilterCount || '0', 10);
      const filterLabel = safeMode === 'advanced' ? 'Advanced Filters' : 'Filters';
      const activeFilterLabel = filterCount > 0
        ? `, ${filterCount} active ${filterCount === 1 ? 'filter' : 'filters'}`
        : '';

      filterTrigger.setAttribute('aria-label', `${filterLabel}${activeFilterLabel}`);
    }

    if (switcher instanceof HTMLElement) {
      switcher.dataset.collectionModeSwitcher = safeMode;
    }

    modeButtons.forEach((button) => {
      const active = button.getAttribute('data-collection-mode-button') === safeMode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    preferenceOptions.forEach((option) => {
      const current = option.querySelector('[data-collection-preference-current]');
      const active = option.getAttribute('data-collection-preference-option') === safeMode;

      if (current instanceof HTMLElement) {
        current.hidden = !active;
      }
    });

    const updateCards = () => {
      const cards = getCards();
      const selected = collection.dataset.collectionBrowsingMode;
      applyModeToCards(cards, selected);
      if (restartProfile && currentMode !== selected) restartProfileFill(cards, selected);
      onApplied();
    };
    if (restartProfile) afterTogglePaint(collection, updateCards);
    else updateCards();

    if (announce && status) {
      status.textContent = safeMode === 'advanced'
        ? 'Coffee Nerd mode selected for coffee cards and filters.'
        : 'Basic mode selected for coffee cards and filters.';
    }
  };

  modeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const mode = button.getAttribute('data-collection-mode-button') || 'basic';

      if (mode === collection.dataset.collectionBrowsingMode) {
        return;
      }

      const scrollAnchor = getCards().find((card) => {
        const rect = card.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight;
      });
      const anchorTop = scrollAnchor?.getBoundingClientRect().top;

      setMode(mode, { announce: true, onApplied: () => {
        if (scrollAnchor && typeof anchorTop === 'number') {
          const delta = scrollAnchor.getBoundingClientRect().top - anchorTop;
          if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
        }
      } });
      storeMode(mode);
    });
  });

  setMode(readStoredMode(), { restartProfile: false });
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      collection.dataset.collectionModeReady = '';
    });
  });

  collection.addEventListener('sigma:collection-view:updated', (event) => {
    if (event.detail?.appendOnly) {
      const appendedCards = (event.detail.cards || [])
        .filter((card) => card instanceof HTMLElement && card.matches('[data-product-card-showcase]'));
      const mode = collection.dataset.collectionBrowsingMode === 'advanced' ? 'advanced' : 'basic';
      applyModeToCards(appendedCards, mode);
      appendedCards.forEach((card) => {
        card.dataset.productCardProfileFill = mode === 'advanced' ? 'on' : 'off';
      });
      return;
    }

    setMode(collection.dataset.collectionBrowsingMode || readStoredMode(), {
      restartProfile: false,
    });
    initializeCollectionSort(collection, event.detail?.nextCollection);
  });

  new MutationObserver(() => {
    setMode(collection.dataset.collectionBrowsingMode || 'basic', {
      restartProfile: false,
    });
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  const preferenceTrigger = collection.querySelector('[data-collection-preference-open]');
  const preferenceDialog = collection.querySelector('[data-collection-preference-dialog]');

  initializeCollectionSheet({
    trigger: preferenceTrigger,
    dialog: preferenceDialog,
    panel: preferenceDialog?.querySelector('[data-collection-preference-panel]'),
    backdrop: preferenceDialog?.querySelector('[data-collection-preference-dismiss]'),
    closeButton: preferenceDialog?.querySelector('[data-collection-preference-close]'),
  });

  const restingTrigger = collection.querySelector('[data-collection-resting-open]');
  const restingDialog = collection.querySelector('[data-collection-resting-dialog]');

  initializeCollectionSheet({
    trigger: restingTrigger,
    dialog: restingDialog,
    panel: restingDialog?.querySelector('[data-collection-resting-panel]'),
    backdrop: restingDialog?.querySelector('[data-collection-resting-dismiss]'),
    closeButton: restingDialog?.querySelector('[data-collection-resting-close]'),
  });
  initializeCollectionSort(collection);
});

const initialCollectionUrl = document.documentElement.dataset.collectionInitialUrl;

if (initialCollectionUrl) {
  const collection = document.querySelector('.collection-shell');

  if (collection instanceof HTMLElement) {
    document.documentElement.dataset.collectionInitialLoading = '';
    updateCollectionView({
      collection,
      url: new URL(initialCollectionUrl),
      updateHistory: false,
    }).finally(() => {
      if (!collectionViewRequest) {
        delete document.documentElement.dataset.collectionArrivalPending;
        delete document.documentElement.dataset.collectionInitialLoading;
      }
    });
  }

  delete document.documentElement.dataset.collectionInitialUrl;
  delete document.documentElement.dataset.collectionInitialFallback;
} else {
  const collection = document.querySelector('.collection-shell');
  const url = new URL(window.location.href);
  if (collection instanceof HTMLElement && (isFreshestCollectionUrl(url) || isArrivalCollection(collection, url))) {
    updateCollectionView({
      collection, url, updateHistory: false,
      responseCache: isArrivalCollection(collection, url) ? { take: () => collection.outerHTML } : null,
    }).then((update) => {
      if (update.committed || update.aborted || !isFreshestCollectionUrl(new URL(window.location.href))) return;
      const fallbackUrl = new URL(window.location.href);
      fallbackUrl.searchParams.delete('freshest');
      window.history.replaceState({ ...window.history.state, sigmaCollectionBrowsing: true }, '', fallbackUrl);
      const sortDialog = collection.querySelector('[data-collection-sort-dialog]');
      if (sortDialog instanceof HTMLDialogElement) syncCollectionSortDialog(sortDialog);
      initializeCollectionLoadMore(collection);
      initializeCollectionShowAll(collection);
    });
  }
}
